import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import _ from 'lodash'
import { AnyConceptStatusesByCid, AnyConceptStatus, ConceptStatusHistory, ConceptStatusValidityByCid, NativePluginEventName } from '@fusion-advantage/backcompat'
import {  ConceptActivationByCid } from "@fusion-advantage/formlib"

export enum FormModal {
	AddAdhocDiagnosis = 'AddAdhocDiagnosis',
}

export interface FormState {
	rendererLoadSerial: number
	activePageIdx: number
	conceptStatus: AnyConceptStatusesByCid
	conceptStatusValidity: ConceptStatusValidityByCid
	conceptActivation: ConceptActivationByCid
	statusHistory: ConceptStatusHistory[]
	calculationErrors: string[]
	importing: boolean
	exporting: boolean
	leaveBehindDialogOpen: boolean
	// rudimentary approach of tracking at present which component
	// instance should have their plugin handler process an inbound native
	// plugin event of a given type.
	// of course this assumes that there would be only one possible handler
	// for an event type at any given time.
	// that is currently the case because the plugins that dispatch
	// ReceiveBarcode and ReceiveSelectedProducts are modals that take up the
	// full screen, there could only be one component that called up the plugin
	// and would be expecting a response from it.
	pluginListenerId: {[key in NativePluginEventName]?: string}
	initialConceptsLoaded: boolean
	modals: FormModal[]
}

const initialState: FormState = {
	rendererLoadSerial: 0,
	activePageIdx: 0,
	conceptStatus: {},
	conceptStatusValidity: {},
	conceptActivation: {},
	statusHistory: [],
	calculationErrors: [],
	importing: false,
	exporting: false,
	leaveBehindDialogOpen: false,
	pluginListenerId: {},
	initialConceptsLoaded: false,
	modals: [],
}

interface SetPluginListenerIdPayload {
	name: NativePluginEventName
	id: string|null
}

const formSlice = createSlice({
	name: 'form',
	initialState,
	reducers: {
		incrementRendererLoadSerial(state) {
			state.rendererLoadSerial++
		},
		setPageIdx(state, action: PayloadAction<number>) {
			state.activePageIdx = action.payload
		},
		// most of the time you don't want to run this directly;
		// use dispatch(submitStatusUpdates()) instead
		setExplicitConceptStatuses(state, action: PayloadAction<AnyConceptStatus[]>) {	
			for(let status of action.payload) {
				state.conceptStatus[status.cid] = status
			}
		},
		setConceptStatusValidities(state, action: PayloadAction<ConceptStatusValidityByCid>) {
			state.conceptStatusValidity = {
				...state.conceptStatusValidity,
				...action.payload,
			}
		},
		setConceptActivations(state, action: PayloadAction<ConceptActivationByCid>) {
			state.conceptActivation = {
				...state.conceptActivation,
				...action.payload,
			}
		},
		setCalculationErrors(state, action: PayloadAction<string[]>) {
			for(let error of action.payload) {
				if(!state.calculationErrors.includes(error))
				state.calculationErrors.push(error)
			}
		},
		unshiftHistoryEntries(state, action: PayloadAction<ConceptStatusHistory[]>) {
			state.statusHistory.unshift(...action.payload)
		},
		setLeaveBehindDialogOpen(state, action: PayloadAction<boolean>) {
			state.leaveBehindDialogOpen = action.payload
		},
		setPluginListenerId(state, action: PayloadAction<SetPluginListenerIdPayload>) {
			const { name, id } = action.payload
			state.pluginListenerId[name] = id ?? undefined
		},
		pushFormModal(state, action: PayloadAction<FormModal>) {
			if(!state.modals.includes(action.payload)) {
				state.modals.push(action.payload)
			}
		},
		unshiftFormModal(state, action: PayloadAction<FormModal>) {
			if(!state.modals.includes(action.payload)) {
				state.modals.unshift(action.payload)
			}
		},
		dismissFormModal(state, action: PayloadAction<FormModal>) {
			state.modals = state.modals.filter(modal => modal !== action.payload)
		},
	},
})

export const {
	incrementRendererLoadSerial,
	setPageIdx,
	setExplicitConceptStatuses,
	setConceptStatusValidities,
	setConceptActivations,
	setCalculationErrors,
	unshiftHistoryEntries,
	setLeaveBehindDialogOpen,
	setPluginListenerId,
	pushFormModal,
	unshiftFormModal,
	dismissFormModal,
} = formSlice.actions
export default formSlice.reducer
